import React, { useEffect, useState } from 'react';
import { LuX, LuGitBranch } from 'react-icons/lu';
import { collection, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useOrganization } from "../../../context/OrgContext";

const NewWorkflowModal = ({ isOpen, onClose, onItemAdded, pathSegments, interfaceId }) => {
  const { institution } = useOrganization();
  const [workflowName, setWorkflowName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  
  // Build collection path to the section containing the interface
  const buildParentSectionCollectionPath = () => {
    if (!institution?.id || !pathSegments) return null;
    
    let collectionPath = `colleges/${institution.id}`;
    
    // For workflow creation, we need the path to the section that contains the interface
    // Remove the last segment (which is the interface) and build path to its parent section
    const parentPath = pathSegments.slice(0, -1);
    
    // Build path step by step: section.collection -> interface -> subsection.collection -> interface...
    for (let i = 0; i < parentPath.length; i += 2) {
      // Add section collection
      collectionPath += `/${parentPath[i]}.section`;
      
      // If there's an interface after this section, add it as a document
      if (i + 1 < parentPath.length) {
        collectionPath += `/${parentPath[i + 1]}`;
      }
    }
    
    return collectionPath;
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setWorkflowName('');
      setIsCreating(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const createSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  const handleCreate = async () => {
    const parentSectionCollectionPath = buildParentSectionCollectionPath();
    if (!workflowName.trim() || !institution?.id || !interfaceId || !parentSectionCollectionPath) return;

    setIsCreating(true);

    try {
      const slug = createSlug(workflowName);

      // Create workflow document in the interface subcollection with slug as document ID
      const workflowRef = doc(db, `${parentSectionCollectionPath}/${interfaceId}/workflows`, slug);

      await setDoc(workflowRef, {
        name: workflowName.trim(),
        slug: slug,
        type: 'workflow',
        status: 'draft',
        createdAt: new Date(),
        updatedAt: new Date(),
        steps: []
      });

      // Call the callback to refresh the parent component
      if (onItemAdded) {
        onItemAdded();
      }

      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error creating workflow:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && workflowName.trim() && !isCreating) {
      handleCreate();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#1a1a1a] border border-[#ffffff10] rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-white">Create New Workflow</h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-[#ffffff10] rounded-lg transition-colors"
            >
              <LuX className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <div className="space-y-4">
            {/* Workflow Preview */}
            <div className="flex items-center space-x-4 p-4 bg-[#ffffff05] border border-[#ffffff10] rounded-lg">
              <div className="p-2 rounded-lg bg-green-400/20">
                <LuGitBranch className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-left">
                <div className="font-medium text-white text-base">
                  Workflow
                </div>
                <div className="text-sm text-[#ffffff70] mt-0.5">
                  Create a new workflow process
                </div>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-[#ffffff90] mb-2">
                Workflow Name
              </label>
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter workflow name..."
                className="w-full px-4 py-3 bg-[#ffffff10] border border-[#ffffff20] rounded-lg text-white placeholder-[#ffffff50] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                autoFocus
                disabled={isCreating}
              />
            </div>

            {/* Create Button */}
            <div className="flex space-x-3 pt-2">
              <button
                onClick={onClose}
                disabled={isCreating}
                className="flex-1 px-4 py-3 bg-[#ffffff10] hover:bg-[#ffffff20] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!workflowName.trim() || isCreating}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create Workflow'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewWorkflowModal;